import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAccount } from '../store';
// Imported from the module and not the `features/auth` barrel: the barrel is a
// `.js` file that pulls the whole feature in, and a route guard should not drag
// components into every chunk that mounts a route subtree.
import { useAuth } from '../features/auth/hooks/useAuth';

/**
 * Guards of the route tree.
 *
 * Extracted from `AppRouter` so a feature that owns its own route subtree (see
 * `features/agent-skills/router.tsx`) can apply the same guard without
 * importing `AppRouter` and creating a cycle. Behaviour is unchanged.
 */

/**
 * Keeps a suspended account out of everything but its own settings, where the
 * suspension can be resolved. Superadmins bypass the restriction.
 */
export const RequireActiveAccount: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { currentAccount } = useAccount();
  const { user } = useAuth();
  const { accountId } = useParams();
  const isAccountSuspended = !!currentAccount?.status && currentAccount.status !== 'active';

  // Superadmins bypass all restrictions
  if (!isAccountSuspended || user?.role === 'superadmin') return children;

  return <Navigate to={`/accounts/${accountId ?? ''}/settings/account`} replace />;
};
